import { useQuery } from "@tanstack/react-query";
import { httpService } from "../../services/httpService";
import { API_CONFIG } from "../../shared/constants";
import { useAuth } from "../helper/useAuth";
import dayjs from "dayjs";
import { IAppDetailsResponse } from "../../features/Apps/interface";

// Default pagination values
const PAGINATION = {
  page: 1,
  per_page: 999999999,
};

interface AppDetailsParams {
  slug: string;
  startDate: Date;
  endDate: Date;
}

export const useAppDetailsData = ({
  slug,
  startDate,
  endDate,
}: AppDetailsParams) => {
  const { selectedIntegration } = useAuth();

  // Get today's date at midnight for cache key
  const today = dayjs().startOf("day").format("YYYY-MM-DD");

  // Format dates for query key to ensure it updates when dates change
  const fromDate = dayjs(startDate).format("YYYY-MM-DD");
  const toDate = dayjs(endDate).format("YYYY-MM-DD");

  return useQuery({
    // Include date range in query key so it refetches when dates change
    queryKey: [
      "appDetails",
      selectedIntegration?.value,
      slug,
      today,
      fromDate,
      toDate,
    ],
    queryFn: async () => {
      const response = await httpService.get<IAppDetailsResponse>(
        API_CONFIG.path.getAppDetails,
        {
          ...PAGINATION,
          from: fromDate,
          to: toDate,
          slug: slug || "",
          tracking_id: selectedIntegration?.value as string,
        }
      );
      return response;
    },
    // Only fetch if we have a selected integration and slug
    enabled: !!selectedIntegration?.value && !!slug,
    // Cache for 24 hours
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
};
